import { useEffect, useMemo, useState } from 'react';

import Dialog from '../../../../organisms/Dialog';
import { TopologySubscription } from '../../../../../services/eventingApi';
import ConsumerSelect from '../shared/ConsumerSelect';
import { shortId } from '../shared/format';
import { acceptsEventType, queueSubscriptions } from '../shared/subscriptions';
import { buttonClass } from '../shared/ui';
import { ReplayRequest } from './history';
import ReplayPreview from './ReplayPreview';

interface ReplayModalProps {
  request: ReplayRequest | null;
  subscriptions: TopologySubscription[];
  submitting: boolean;
  onClose: () => void;
  onConfirm: (request: ReplayRequest, target: string) => void;
}

const ReplayModal = ({ request, subscriptions, submitting, onClose, onConfirm }: ReplayModalProps) => {
  const targets = useMemo(
    () =>
      request
        ? queueSubscriptions(subscriptions).filter((subscription) =>
            request.events.some((event) => acceptsEventType(subscription, event.message.eventType)),
          )
        : [],
    [request, subscriptions],
  );
  const [target, setTarget] = useState('');

  useEffect(() => setTarget(targets[0]?.consumer ?? ''), [targets]);

  if (!request) return null;

  const body = (
    <div className="space-y-4 text-sm dark:text-gray-200">
      <p>
        {request.events.length} evento(s) do histórico entre {request.window.from} e {request.window.to} serão publicados de novo no SNS, com o mesmo eventId,{' '}
        <code>delivery = replay</code> e <code>target</code> = consumidor escolhido. Só a fila desse consumidor recebe, e o Firehose não arquiva replay.
      </p>
      <ConsumerSelect targets={targets} value={target} onChange={setTarget} />
      {targets.length === 0 && <p className="text-red-600">Nenhum consumidor com fila assina esses tipos de evento.</p>}
      <ReplayPreview event={request.events[0]} target={target} />
      <details className="text-xs">
        <summary className="cursor-pointer">Eventos selecionados</summary>
        <ul className="mt-2 max-h-32 space-y-1 overflow-auto font-mono">
          {request.events.map((event) => (
            <li key={event.message.eventId}>
              {event.message.eventType} · {shortId(event.aggregate.aggregateId)} · v{event.aggregate.aggregateVersion}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-2">
      <button type="button" className={buttonClass.cancel} onClick={onClose}>
        Cancelar
      </button>
      <button type="button" className={buttonClass.secondary} disabled={!target || submitting} onClick={() => onConfirm(request, target)}>
        {submitting ? 'Publicando…' : `Publicar ${request.events.length} replay(s)`}
      </button>
    </div>
  );

  return <Dialog id="event-platform-replay" show onClose={onClose} header="Reprocessar no consumidor (replay dirigido)" body={body} footer={footer} size="lg" />;
};

export default ReplayModal;
